import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import {
  ShieldCheck, ShieldAlert, ShieldQuestion, Search, UserCheck,
  Users, Loader2, CheckCircle, XCircle, Clock
} from "lucide-react";

const STATUS_CONFIG = {
  verified: { icon: <ShieldCheck className="w-4 h-4" />, color: "bg-green-100 text-green-700 border-green-200", label: "Verified" },
  pending: { icon: <Clock className="w-4 h-4" />, color: "bg-amber-100 text-amber-700 border-amber-200", label: "Pending" },
  unverified: { icon: <ShieldAlert className="w-4 h-4" />, color: "bg-red-100 text-red-700 border-red-200", label: "Unverified" }
};

const OwnerVerificationPage = () => {
  const { token } = useAuth();
  const [owners, setOwners] = useState([]);
  const [counts, setCounts] = useState({ verified: 0, pending: 0, unverified: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => { fetchOwners(); }, [token]);

  const fetchOwners = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/owner-verification/all`, { headers: { Authorization: `Bearer ${token}` } });
      setOwners(res.data.owners || []);
      setCounts(res.data.counts || { verified: 0, pending: 0, unverified: 0 });
    } catch { toast.error("Failed to load owners"); } finally { setLoading(false); }
  };

  const handleVerify = async (ownerId) => {
    setActionLoading(ownerId);
    try {
      await axios.post(`${API}/owner-verification/verify/${ownerId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Owner verified");
      fetchOwners();
    } catch { toast.error("Failed to verify"); } finally { setActionLoading(""); }
  };

  const handleSetPending = async (ownerId) => {
    setActionLoading(ownerId);
    try {
      await axios.post(`${API}/owner-verification/set-pending/${ownerId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Owner set to pending");
      fetchOwners();
    } catch { toast.error("Failed"); } finally { setActionLoading(""); }
  };

  const handleUnverify = async (ownerId) => {
    setActionLoading(ownerId);
    try {
      await axios.post(`${API}/owner-verification/unverify/${ownerId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Owner unverified");
      fetchOwners();
    } catch { toast.error("Failed"); } finally { setActionLoading(""); }
  };

  const handleBulkVerify = async () => {
    if (selectedIds.length === 0) return;
    try {
      await axios.post(`${API}/owner-verification/bulk-verify`, { owner_ids: selectedIds }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${selectedIds.length} owners verified`);
      setSelectedIds([]);
      fetchOwners();
    } catch { toast.error("Bulk verify failed"); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    const filtered = getFiltered();
    const allIds = filtered.map(o => o.owner_id);
    setSelectedIds(prev => prev.length === allIds.length ? [] : allIds);
  };

  const getFiltered = () => {
    return owners.filter(o => {
      const matchSearch = !search || `${o.first_name} ${o.last_name} ${o.email_primary}`.toLowerCase().includes(search.toLowerCase());
      const matchFilter = filter === "all" || o.verification_status === filter;
      return matchSearch && matchFilter;
    });
  };

  const filtered = getFiltered();

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      <main className="flex-1 p-6" data-testid="owner-verification-page">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <UserCheck className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Owner Verification</h1>
              <p className="text-sm text-muted-foreground">Only verified owners can submit complaints, DRC applications, and formal correspondence.</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="cursor-pointer border-2" onClick={() => setFilter("verified")} data-testid="count-verified">
              <CardContent className="py-4 flex items-center gap-3">
                <ShieldCheck className="w-8 h-8 text-green-600" />
                <div><p className="text-2xl font-bold">{counts.verified}</p><p className="text-sm text-muted-foreground">Verified</p></div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer border-2" onClick={() => setFilter("pending")} data-testid="count-pending">
              <CardContent className="py-4 flex items-center gap-3">
                <Clock className="w-8 h-8 text-amber-600" />
                <div><p className="text-2xl font-bold">{counts.pending}</p><p className="text-sm text-muted-foreground">Pending</p></div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer border-2" onClick={() => setFilter("unverified")} data-testid="count-unverified">
              <CardContent className="py-4 flex items-center gap-3">
                <ShieldAlert className="w-8 h-8 text-red-600" />
                <div><p className="text-2xl font-bold">{counts.unverified}</p><p className="text-sm text-muted-foreground">Unverified</p></div>
              </CardContent>
            </Card>
          </div>

          {/* Search & Bulk Actions */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search owners..." className="pl-9" data-testid="search-owners" />
            </div>
            <Button variant="outline" size="sm" onClick={() => setFilter("all")} className={filter === "all" ? "bg-primary text-primary-foreground" : ""}>All</Button>
            {selectedIds.length > 0 && (
              <Button size="sm" onClick={handleBulkVerify} data-testid="bulk-verify-btn">
                <CheckCircle className="w-4 h-4 mr-1" /> Verify {selectedIds.length} Selected
              </Button>
            )}
          </div>

          {/* Owner List */}
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-muted-foreground border-b">
                <input type="checkbox" className="rounded" checked={selectedIds.length === filtered.length && filtered.length > 0} onChange={selectAll} />
                <span className="flex-1">Owner</span>
                <span className="w-24 text-center">Status</span>
                <span className="w-64 text-right">Actions</span>
              </div>
              {filtered.map(owner => {
                const status = owner.verification_status || "unverified";
                const cfg = STATUS_CONFIG[status];
                return (
                  <div key={owner.owner_id} className="flex items-center gap-2 px-4 py-3 border rounded-md bg-card hover:bg-muted/30 transition-colors" data-testid={`owner-row-${owner.owner_id}`}>
                    <input type="checkbox" className="rounded" checked={selectedIds.includes(owner.owner_id)} onChange={() => toggleSelect(owner.owner_id)} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{owner.first_name} {owner.last_name}</p>
                      <p className="text-xs text-muted-foreground">{owner.email_primary}</p>
                    </div>
                    <Badge className={`w-24 justify-center ${cfg.color}`} data-testid={`status-badge-${owner.owner_id}`}>
                      {cfg.icon}<span className="ml-1">{cfg.label}</span>
                    </Badge>
                    <div className="w-64 flex items-center justify-end gap-1">
                      {actionLoading === owner.owner_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          {status !== "verified" && (
                            <Button size="sm" variant="outline" className="text-green-700 border-green-300 h-7 text-xs" onClick={() => handleVerify(owner.owner_id)} data-testid={`verify-btn-${owner.owner_id}`}>
                              <ShieldCheck className="w-3 h-3 mr-1" /> Verify
                            </Button>
                          )}
                          {status !== "pending" && (
                            <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 h-7 text-xs" onClick={() => handleSetPending(owner.owner_id)} data-testid={`pending-btn-${owner.owner_id}`}>
                              <Clock className="w-3 h-3 mr-1" /> Pending
                            </Button>
                          )}
                          {status === "verified" && (
                            <Button size="sm" variant="outline" className="text-red-700 border-red-300 h-7 text-xs" onClick={() => handleUnverify(owner.owner_id)} data-testid={`unverify-btn-${owner.owner_id}`}>
                              <XCircle className="w-3 h-3 mr-1" /> Revoke
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && <p className="text-center py-8 text-muted-foreground">No owners match your search</p>}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default OwnerVerificationPage;
